/**
 * Palais des glaces (author's addition, an eighth map; « glace » is both ice and mirror): a palace of ice on a rock
 * island, a maze of galleries over four floors. Every wall of ice sends the laser back; the steep ramps between the
 * floors, the corners cut at 45° and the diamond pillars of the halls turn it by a right angle. Some walls are of rock
 * and stop it. Gates at both ends on the ground floor, ramps up to the roof and skylights. The ice is slippery, melts
 * under the flame and blows up in the lava. The seed changes the maze.
 *
 * The maze is a grid of COLS × LEVELS cells, linked side by side (an opening) or from a floor to the one above (a
 * ramp you can walk up): a random spanning tree, so that every gallery can be reached on foot, then a few more links.
 */
import { Physique, nextInt, type Rng } from '@invaders/sim';
import { SEA_LEVEL_FROM_BOTTOM, plainMap, type MapState } from '../state';
import { type Gen, island, layer, rect, sea, set, settle } from './common';

const { EMPTY, ROCK, ICE, GRAVEL } = Physique;

const COLS = 13;
const LEVELS = 4;
/** Width of a cell, and height from a floor to the next. */
const CELL = 52;
/** Height of the galleries: 39 (26 before, half as high again by the author's choice); floors of 13 between them. */
const HEIGHT = 39;
/** Ice left on each side of a cell without an opening: walls of 8 between two cells. */
const WALL = 4;
/** The palace's outer walls. */
const OUTER = 12;
/** Room above a ramp's floor: a unit's body on a slope of about 50° (38, then half as much again). */
const RAMP_CLEAR = 57;
/** Corners cut at 45° under the ceiling of a gallery that ends on a wall. */
const CHAMFER = 8;
/** Diamond pillars in the halls: |dx| + |dy| ≤ this. */
const DIAMOND = 8;

interface Cell {
  /** Opening on the right (to the cell c + 1 of the same floor). */
  right: boolean;
  /** Ramp up to the floor above: 0 none, 1 rising to the right, -1 to the left. */
  ramp: number;
}

/** Where the cells are on a map of width w and height h (also for the tests). */
export function palaceLayout(w: number, h: number) {
  const ground = h - SEA_LEVEL_FROM_BOTTOM - 12;
  const x0 = (w - COLS * CELL) >> 1;
  return {
    cols: COLS, levels: LEVELS, ground, x0, x1: x0 + COLS * CELL,
    /** First and last column inside cell c. */
    left: (c: number) => x0 + c * CELL + WALL,
    right: (c: number) => x0 + (c + 1) * CELL - WALL - 1,
    /** Floor row of level l (0 = top): the row under its gallery. Level -1 is the roof. */
    floor: (l: number) => ground - (LEVELS - 1 - l) * CELL,
    height: HEIGHT,
  };
}

function shuffle<T>(rng: Rng, list: T[]): T[] {
  for (let i = list.length - 1; i > 0; i--) {
    const j = nextInt(rng, i + 1);
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

export function generatePalace(g: Gen): MapState {
  const { w, rng, seaY } = g;
  const coastL = Math.trunc((w * 9) / 100) + nextInt(rng, 9) - 4;
  const coastR = Math.trunc((w * 91) / 100) + nextInt(rng, 9) - 4;
  const { ground, x0, x1, left, right, floor } = palaceLayout(w, g.h);
  const surface = island(g, coastL, coastR, 36, () => seaY - ground);
  const roof = floor(-1);

  // Gravel on the beaches, then the block of ice on the rock.
  layer(g, surface, coastL - 30, x0 - OUTER - 1, () => 3, GRAVEL);
  layer(g, surface, x1 + OUTER, coastR + 30, () => 3, GRAVEL);
  rect(g, x0 - OUTER, roof, x1 + OUTER - 1, ground - 1, ICE);

  // --- the maze -----------------------------------------------------------------------------------
  const cells: Cell[] = [];
  for (let i = 0; i < COLS * LEVELS; i++) cells.push({ right: false, ramp: 0 });
  const at = (c: number, l: number) => cells[l * COLS + c];
  const parent = cells.map((_, i) => i);
  const find = (i: number): number => { while (parent[i] !== i) i = parent[i] = parent[parent[i]]; return i; };
  type Link = { c: number; l: number; ramp: number };
  const candidates: Link[] = [];
  for (let l = 0; l < LEVELS; l++) {
    for (let c = 0; c < COLS; c++) {
      if (c < COLS - 1) candidates.push({ c, l, ramp: 0 });
      if (l > 0) { candidates.push({ c, l, ramp: 1 }); candidates.push({ c, l, ramp: -1 }); }
    }
  }
  // The gates: the ground floor opens to the outside at both ends (a ramp must not wall them up).
  const opensLeft = (c: number, l: number) => (c > 0 && at(c - 1, l).right) || (c === 0 && l === LEVELS - 1);
  const opensRight = (c: number, l: number) => at(c, l).right || (c === COLS - 1 && l === LEVELS - 1);
  /**
   * A ramp fills the high half of its cell (its rising side): no opening there. One per cell, and the ramps of a
   * column alternate (one comes out on the floor above where the next one would rise).
   */
  const allowed = (k: Link) => {
    if (k.ramp === 0) {
      const a = at(k.c, k.l);
      const b = at(k.c + 1, k.l);
      return a.right === false && a.ramp !== 1 && b.ramp !== -1;
    }
    const a = at(k.c, k.l);
    if (a.ramp !== 0 || (k.ramp > 0 ? opensRight(k.c, k.l) : opensLeft(k.c, k.l))) return false;
    if (at(k.c, k.l - 1).ramp === k.ramp) return false;
    return k.l + 1 >= LEVELS || at(k.c, k.l + 1).ramp !== k.ramp;
  };
  const accept = (k: Link) => {
    if (k.ramp === 0) at(k.c, k.l).right = true; else at(k.c, k.l).ramp = k.ramp;
    const a = find(k.l * COLS + k.c);
    const b = find(k.ramp === 0 ? k.l * COLS + k.c + 1 : (k.l - 1) * COLS + k.c);
    parent[a] = b;
  };
  const order = shuffle(rng, candidates);
  for (const k of order) {
    const a = find(k.l * COLS + k.c);
    const b = find(k.ramp === 0 ? k.l * COLS + k.c + 1 : (k.l - 1) * COLS + k.c);
    if (a !== b && allowed(k)) accept(k);
  }
  // Every cell is reached; a few more links make loops (more ways round, more corridors).
  for (const k of order) if (nextInt(rng, 100) < 22 && allowed(k)) accept(k);
  // The roof: two ramps up from the top floor, one in each half.
  for (const half of [0, 1]) {
    const from = half === 0 ? 1 : 7;
    for (let tries = 0; tries < 6; tries++) {
      const c = from + nextInt(rng, 5);
      const dir = nextInt(rng, 2) === 0 ? 1 : -1;
      if (at(c, 0).ramp !== 0 || (dir > 0 ? opensRight(c, 0) : opensLeft(c, 0)) || at(c, 1).ramp === dir) continue;
      at(c, 0).ramp = dir;
      break;
    }
  }

  // --- carving --------------------------------------------------------------------------------------
  // The galleries, with their openings (and the gates through the outer walls).
  for (let l = 0; l < LEVELS; l++) {
    const top = floor(l) - HEIGHT;
    for (let c = 0; c < COLS; c++) {
      rect(g, left(c), top, right(c), floor(l) - 1, EMPTY);
      if (at(c, l).right) rect(g, right(c) + 1, top, left(c + 1) - 1, floor(l) - 1, EMPTY);
      else if (c < COLS - 1 && nextInt(rng, 4) === 0) rect(g, right(c) + 1, top, left(c + 1) - 1, floor(l) - 1, ROCK);
    }
  }
  rect(g, x0 - OUTER, floor(LEVELS - 1) - HEIGHT, left(0) - 1, floor(LEVELS - 1) - 1, EMPTY);
  rect(g, right(COLS - 1) + 1, floor(LEVELS - 1) - HEIGHT, x1 + OUTER - 1, floor(LEVELS - 1) - 1, EMPTY);

  // Corners cut at 45° under the ceiling, where a gallery ends on a wall of ice: mirrors at a right angle.
  for (let l = 0; l < LEVELS; l++) {
    const top = floor(l) - HEIGHT;
    for (let c = 0; c < COLS; c++) {
      for (let k = 0; k < CHAMFER; k++) {
        if (!opensLeft(c, l)) rect(g, left(c), top + k, left(c) + CHAMFER - 1 - k, top + k, ICE, EMPTY);
        if (!opensRight(c, l)) rect(g, right(c) - CHAMFER + 1 + k, top + k, right(c), top + k, ICE, EMPTY);
      }
    }
  }

  // The ramps: a wedge of ice in the lower gallery, a steep tunnel through the floor above.
  const ramp = (c: number, l: number, dir: number) => {
    const xs = dir > 0 ? left(c) : right(c);
    const span = right(c) - left(c);
    const ceiling = l - 1 >= 0 ? floor(l - 1) - HEIGHT : 0;
    for (let i = 0; i <= span; i++) {
      const x = xs + i * dir;
      const y = floor(l) - Math.trunc((i * CELL) / span);
      for (let yy = Math.max(ceiling, y - RAMP_CLEAR); yy < y; yy++) set(g, x, yy, EMPTY);
      for (let yy = y; yy < floor(l); yy++) set(g, x, yy, ICE);
    }
  };
  // From the top down: each tunnel cuts a step into the wedge of the ramp above it (a staircase).
  for (let l = 0; l < LEVELS; l++) for (let c = 0; c < COLS; c++) if (at(c, l).ramp !== 0) ramp(c, l, at(c, l).ramp);

  // Skylights down into the top floor.
  let skylights = 2 + nextInt(rng, 2);
  for (let tries = 0; tries < 20 && skylights > 0; tries++) {
    const c = 1 + nextInt(rng, COLS - 2);
    if (at(c, 0).ramp !== 0) continue;
    const mid = (left(c) + right(c)) >> 1;
    rect(g, mid - 10, roof, mid + 10, floor(0) - HEIGHT - 1, EMPTY);
    skylights--;
  }

  // Halls: a gallery that leads nowhere else opens onto the one under it (a drop of two floors), a diamond pillar of
  // ice floating in the middle.
  let halls = 2 + nextInt(rng, 2);
  for (const c of shuffle(rng, cells.map((_, i) => i % COLS))) {
    if (halls === 0) break;
    const l = 1 + nextInt(rng, LEVELS - 1);
    const upper = at(c, l - 1);
    const links = (opensLeft(c, l - 1) ? 1 : 0) + (opensRight(c, l - 1) ? 1 : 0) + (upper.ramp !== 0 ? 1 : 0);
    if (at(c, l).ramp !== 0 || upper.ramp !== 0 || links > 1) continue;
    rect(g, left(c), floor(l - 1) - 1, right(c), floor(l) - HEIGHT - 1, EMPTY);
    const cx = (left(c) + right(c)) >> 1;
    const cy = floor(l) - HEIGHT - (CELL >> 1);
    for (let dy = -DIAMOND; dy <= DIAMOND; dy++) {
      const half = DIAMOND - Math.abs(dy);
      rect(g, cx - half, cy + dy, cx + half, cy + dy, ICE);
    }
    halls--;
  }

  // Towers on the roof at both ends, with a pointed top.
  for (const tx of [x0 - OUTER, x1 + OUTER - 22]) {
    rect(g, tx, roof - 26, tx + 21, roof - 1, ICE);
    for (let k = 0; k < 11; k++) rect(g, tx + k, roof - 27 - k, tx + 21 - k, roof - 27 - k, ICE);
  }

  sea(g);
  settle(g);
  return plainMap('palace');
}
